import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Home, KeyRound, FileSignature, Trees, Palmtree, Hammer, Armchair, Compass } from "lucide-react";
import { Reveal } from "./Reveal";
import { IMAGES, HOME_SERVICES } from "../lib/data";

const ICONS = { Home, KeyRound, FileSignature, Trees, Palmtree, Hammer, Armchair, Compass };

export const ConnexaHomeSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section id="connexa-home" data-testid="showcase-home" className="py-16 md:py-28">
      {/* Full-width editorial villa image */}
      <div ref={ref} className="relative h-[60vh] w-full overflow-hidden md:h-[85vh]">
        <motion.img
          style={{ y, scale: 1.16 }}
          src={IMAGES.homeVillaEditorial}
          alt="Villa moderna italiana di lusso"
          className="absolute inset-0 h-full w-full object-cover img-cinematic"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/25 to-black/40" />
        <div className="absolute inset-x-0 bottom-0">
          <div className="cx-container pb-12 md:pb-16">
            <Reveal>
              <p className="overline">Connexa Home</p>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-4 font-display text-5xl font-light leading-none text-[var(--cx-white)] md:text-8xl">
                Connexa <span className="gold-text italic">Home</span>
              </h2>
            </Reveal>
          </div>
        </div>
      </div>

      {/* Emotional intro */}
      <div className="cx-container mt-16 md:mt-24">
        <div className="grid gap-8 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <p className="font-display text-3xl font-light italic leading-tight text-[var(--cx-white)] md:text-4xl">
              Trovare il luogo giusto da chiamare casa.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <p className="font-body text-lg leading-relaxed text-[var(--cx-white)]/75">
              Una casa non è soltanto un immobile: è dove costruisci la tua vita, i tuoi ricordi,
              il tuo senso di quiete. Per questo non ci limitiamo a cercare: ascoltiamo chi sei,
              immaginiamo come vuoi vivere e ci occupiamo di tutto — dalla ricerca alla trattativa,
              fino alle chiavi in mano. Tu devi solo riconoscere il momento in cui dirai: “è questa”.
            </p>
          </Reveal>
        </div>
      </div>

      {/* Services with elegant gold icons */}
      <div id="connexa-home-servizi" className="cx-container mt-16 md:mt-20">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {HOME_SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            return (
              <Reveal key={s.title} delay={(i % 4) * 0.06}>
                <div className="group flex h-full flex-col border border-[var(--cx-border)] bg-[var(--cx-black)] p-8 transition-colors duration-500 hover:border-[var(--cx-gold)]/40 hover:bg-[var(--cx-surface)]">
                  <Icon size={30} strokeWidth={1.2} className="text-[var(--cx-gold)] transition-transform duration-500 group-hover:-translate-y-1" />
                  <h3 className="mt-6 font-display text-2xl font-light text-[var(--cx-white)]">{s.title}</h3>
                  <p className="mt-2 font-body text-sm leading-relaxed text-[var(--cx-muted)]">{s.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>

      {/* CTA */}
      <div className="cx-container mt-14 md:mt-16">
        <Reveal>
          <Link
            to="/connexa-home"
            data-testid="showcase-home-link"
            className="group inline-flex items-center gap-4 bg-[var(--cx-gold)] px-9 py-4 font-body text-sm tracking-wide text-black transition-all duration-300 hover:bg-[var(--cx-gold-bright)]"
          >
            Scopri Connexa Home
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};
